import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, ArrowRight } from "lucide-react";
import { useBrief } from "@/context/BriefContext";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { LEGAL_LINKS } from "@/lib/legalLinks";

// ---------------------------------------------------------------------------
// LIGHT EXECUTIVE SYSTEM — shared public chrome (header + footer).
// Self-scoped under .exec-scope. Jost + Cormorant. The & is always Cormorant
// italic copper (.exec-amp). One Ink anchor section per page lives in the
// footer CTA band. Hub/Admin keep the legacy Header/Footer (see App.js).
// ---------------------------------------------------------------------------

const NAV = [
  { to: "/work", label: "Work", testid: "site-nav-work-link" },
  { to: "/services", label: "Services", testid: "site-nav-services-link" },
  { to: "/resources", label: "Resources", testid: "site-nav-resources-link" },
  { to: "/events", label: "Events", testid: "site-nav-events-link" },
  { to: "/about", label: "About", testid: "site-nav-about-link" },
];

// Typographic wordmark. Copper appears only on the Cormorant italic ampersand.
export function ExecWordmark({ className = "" }) {
  return (
    <span
      className={`font-light tracking-[0.22em] whitespace-nowrap ${className}`}
      aria-label="Copper and Vine"
    >
      COPPER <span className="exec-amp">&amp;</span> VINE
    </span>
  );
}

export function ExecHeader() {
  const { openBrief } = useBrief();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const navLink = (item, mobile = false) => (
    <NavLink
      key={item.to}
      to={item.to}
      data-testid={item.testid}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        `text-[11.5px] uppercase tracking-[0.2em] font-normal transition-colors duration-300 ${
          mobile ? "py-3 block" : ""
        } ${
          isActive
            ? "text-[color:var(--copper)]"
            : "text-[color:var(--grey)] hover:text-[color:var(--copper)]"
        }`
      }
    >
      {item.label}
    </NavLink>
  );

  return (
    <header
      data-testid="site-header"
      className="exec-scope sticky top-0 z-40 border-b border-[color:var(--rule)] bg-[color:var(--paper)]/95 backdrop-blur-sm"
    >
      <div className="exec-container">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" aria-label="Copper and Vine home" className="flex items-center">
            <ExecWordmark className="text-[20px]" />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((i) => navLink(i))}
            {user ? (
              <Link
                to="/hub"
                data-testid="site-nav-hub-link"
                className="text-[11.5px] uppercase tracking-[0.2em] font-normal text-[color:var(--grey)] hover:text-[color:var(--copper)] transition-colors duration-300 inline-flex items-center gap-1.5"
              >
                <User size={13} /> Hub
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="site-nav-login-link"
                className="text-[11.5px] uppercase tracking-[0.2em] font-normal text-[color:var(--grey)] hover:text-[color:var(--copper)] transition-colors duration-300"
              >
                Sign in
              </Link>
            )}
            <button
              data-testid="global-brief-request-button"
              className="exec-btn exec-btn-primary !py-3 !px-6"
              onClick={() => {
                track("door_click", { target: "brief_request", location: "header" });
                openBrief("general");
              }}
            >
              Request the brief
            </button>
          </nav>

          <button
            className="lg:hidden p-2 -mr-2 text-[color:var(--ink)]"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[color:var(--rule)] bg-[color:var(--paper)]">
          <div className="exec-container py-5">
            {NAV.map((i) => navLink(i, true))}
            {user ? (
              <Link
                to="/hub"
                data-testid="site-nav-hub-link-mobile"
                onClick={() => setOpen(false)}
                className="text-[11.5px] uppercase tracking-[0.2em] font-normal text-[color:var(--grey)] hover:text-[color:var(--copper)] py-3 block"
              >
                The Hub
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="site-nav-login-link-mobile"
                onClick={() => setOpen(false)}
                className="text-[11.5px] uppercase tracking-[0.2em] font-normal text-[color:var(--grey)] hover:text-[color:var(--copper)] py-3 block"
              >
                Sign in
              </Link>
            )}
            <button
              data-testid="global-brief-request-button-mobile"
              className="exec-btn exec-btn-primary w-full mt-5"
              onClick={() => {
                setOpen(false);
                track("door_click", { target: "brief_request", location: "header_mobile" });
                openBrief("general");
              }}
            >
              Request the brief
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export function ExecFooter() {
  const { openBrief } = useBrief();
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  return (
    <footer className="exec-scope" data-testid="site-footer">
      {/* The single Ink anchor section per page — paper text, inverted buttons */}
      <section
        className="exec-ink-section bg-[color:var(--ink)] text-[color:var(--paper)] py-24"
        data-testid="site-footer-cta-band"
      >
        <div className="exec-container">
          <div className="max-w-[820px]">
            <p className="font-normal text-[11px] uppercase tracking-[0.3em] text-[color:var(--copper)]">
              Start where the pain is
            </p>
            <h2 className="font-extralight text-[clamp(32px,4.4vw,54px)] leading-[1.1] mt-6">
              The decision is expensive. The call is{" "}
              <span className="exec-serif italic font-medium text-[color:var(--copper)]">yours.</span>
            </h2>
            <p className="exec-serif font-normal text-[clamp(18px,2vw,23px)] leading-[1.5] mt-6 max-w-[48ch] text-[color:rgba(250,249,246,0.9)]">
              Start a Sprint today, or request the brief and we&rsquo;ll send the detail first.
              The next move is yours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button
                data-testid="footer-cta-button"
                className="exec-btn exec-btn-light"
                onClick={() => {
                  track("door_click", { target: "sprint", location: "footer" });
                  navigate("/services/founder-tech-clarity-sprint");
                }}
              >
                Start a Sprint
              </button>
              <button
                data-testid="footer-brief-button"
                className="exec-btn exec-btn-outline-light"
                onClick={() => {
                  track("door_click", { target: "brief_request", location: "footer" });
                  openBrief("general");
                }}
              >
                Request the brief
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Light base footer — rule motif, wordmark, nav + legal links */}
      <div className="py-14 bg-[color:var(--paper)]">
        <div className="exec-container">
          <div className="exec-rule mb-9" />
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <ExecWordmark className="text-[18px]" />
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.2em] text-[color:var(--grey)] font-normal">
              {NAV.map((i) => (
                <Link
                  key={i.to}
                  to={i.to}
                  className="hover:text-[color:var(--copper)] transition-colors duration-300"
                >
                  {i.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="exec-rule mt-9 mb-7" />

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <p className="text-[10.5px] tracking-[0.16em] uppercase text-[color:var(--grey)] font-light">
              &copy; {year} COPPER <span className="exec-amp">&amp;</span> VINE &nbsp;&middot;&nbsp;
              EXECUTIVE TECHNOLOGY ADVISORY SINCE 2003
            </p>
            <div className="flex flex-wrap gap-x-7 gap-y-2 text-[10.5px] uppercase tracking-[0.16em] text-[color:var(--grey)] font-normal">
              <a
                href={LEGAL_LINKS.privacy}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-privacy-link"
                className="hover:text-[color:var(--copper)] transition-colors duration-300"
              >
                Privacy
              </a>
              <a
                href={LEGAL_LINKS.terms}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-terms-link"
                className="hover:text-[color:var(--copper)] transition-colors duration-300"
              >
                Terms
              </a>
              <a
                href={LEGAL_LINKS.cookies}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-cookies-link"
                className="hover:text-[color:var(--copper)] transition-colors duration-300"
              >
                Cookies
              </a>
              <a
                href="#"
                className="termly-display-preferences hover:text-[color:var(--copper)] transition-colors duration-300"
                data-testid="footer-consent-preferences-link"
              >
                Consent Preferences
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
